const PLACEHOLDER_ONLY = /^(?:\[?(?:bolagsnamn|company name|owner|\u00e4gare|kund|customer|text|rubrik|heading|placeholder)\]?|(?:skriv|ange|fyll i|l\u00e4gg till)\b.*(?:h\u00e4r|text|rubrik|uppgift)|write\b.*(?:here|text|heading|paragraph)|lorem ipsum\b.*)[.!]?$/i;

function childText(child) {
  if (!child || typeof child !== "object") return "";
  return String(child.text || child.body || child.title || child.caption || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;|&#39;|&apos;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isPlaceholderText(text) {
  const value = String(text || "").replace(/\s+/g, " ").trim();
  return !value || PLACEHOLDER_ONLY.test(value);
}

export function hasRenderableIMContent(section) {
  if (!section || typeof section !== "object") return false;
  if (section.origin === "template") return false;
  const children = [
    ...(Array.isArray(section.children) ? section.children : []),
    ...(Array.isArray(section.cells) ? section.cells.flatMap((cell) => cell.children || []) : []),
  ];
  return children.some((child) => {
    if (!child || typeof child !== "object") return false;
    if (["title", "heading", "divider", "spacer"].includes(child.kind)) return false;
    if (["financialTable", "ownershipTable"].includes(child.kind)) {
      return Array.isArray(child.rows) && child.rows.some((row) =>
        Array.isArray(row) ? row.some((cell) => !isPlaceholderText(cell)) :
          Object.values(row || {}).some((cell) => !isPlaceholderText(cell)));
    }
    if (["revenueChart", "image"].includes(child.kind)) {
      return !!(child.imageUrl || child.url || (child.periods && child.periods.length));
    }
    if (child.kind === "imageCard") {
      return !!(child.imageUrl && !isPlaceholderText(child.text || child.title));
    }
    if (child.kind === "button") return !isPlaceholderText(child.text) && child.url !== "#";
    const text = childText(child);
    return text.length > 0 && !isPlaceholderText(text);
  });
}

export function selectFinalIMSections(sections) {
  const result = [];
  const seen = new Set();
  for (const section of Array.isArray(sections) ? sections : []) {
    if (!section || ["dividerSection", "spacerSection"].includes(section.type)) continue;
    if (section.type !== "coverSection" && !hasRenderableIMContent(section)) continue;
    const key = section.documentSectionId || section.sectionId;
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    result.push(section);
  }
  return result;
}

export function applyPresentationToIMSections(sections, template) {
  const layout = Array.isArray(template?.layout) ? template.layout : [];
  const slots = new Map();
  const unsupported = new Set();
  for (const row of layout) {
    for (const cell of row.cells || []) {
      for (const item of cell.items || []) {
        if (item.type !== "section" || !item.sectionId) continue;
        const id = String(item.sectionId);
        if (item.unsupported === true) {
          unsupported.add(id);
          continue;
        }
        if (!slots.has(id)) slots.set(id, { row, cell, item });
      }
    }
  }
  const theme = template?.theme || {};
  const styleKeys = ["backgroundColor", "textColor", "fontFamily", "fontSize", "padding", "margin", "marginBottom", "borderColor"];
  const presented = (Array.isArray(sections) ? sections : [])
    .filter((section) => !unsupported.has(String(section.documentSectionId || section.sectionId || "")))
    .map((section) => {
      const baseStyle = section._presentationBaseStyle || Object.fromEntries(
        styleKeys.map((key) => [key, section[key]]),
      );
      const baseSection = { ...section, ...baseStyle };
      const slot = slots.get(String(section.documentSectionId || section.sectionId || ""));
      const visual = {
        ...(slot?.row?.styles || {}),
        ...(slot?.cell?.styles || {}),
        ...(slot?.item?.styles || {}),
      };
      return {
        ...baseSection,
        _presentationBaseStyle: baseStyle,
        ...visual,
        ...(theme.font_body ? { fontFamily: theme.font_body } : {}),
        ...(theme.text_color ? { textColor: theme.text_color } : {}),
      };
    });
  const preferred = template?.section_order || template?.theme?.section_order;
  if (!Array.isArray(preferred) || !preferred.length) return presented;
  const rank = new Map(preferred.map((id, index) => [String(id), index]));
  return presented.map((section, index) => ({ section, index }))
    .sort((a, b) => {
      if (a.section.type === "coverSection") return b.section.type === "coverSection" ? a.index - b.index : -1;
      if (b.section.type === "coverSection") return 1;
      const aid = String(a.section.documentSectionId || a.section.sectionId || "");
      const bid = String(b.section.documentSectionId || b.section.sectionId || "");
      return (rank.get(aid) ?? 9999) - (rank.get(bid) ?? 9999) || a.index - b.index;
    }).map((entry) => entry.section);
}

export function createIMPresentationLayout(sections) {
  const rows = [];
  for (const section of Array.isArray(sections) ? sections : []) {
    if (section.type === "coverSection") {
      rows.push({
        kind: "cover",
        title: "[Bolagsnamn]",
        styles: {
          backgroundColor: section.backgroundColor,
          textColor: section.textColor,
        },
      });
      continue;
    }
    const sectionId = section.documentSectionId || section.sectionId;
    if (!sectionId || section.origin === "template") continue;
    const styles = {};
    for (const key of ["backgroundColor", "textColor", "fontFamily", "fontSize", "padding", "margin", "marginBottom", "borderColor"]) {
      if (section[key] != null) styles[key] = section[key];
    }
    rows.push({
      kind: "structure",
      styles,
      layout: "1col",
      cells: [{ items: [{ type: "section", sectionId, styles }] }],
    });
  }
  return rows;
}
